<script>
  import { onDestroy } from "svelte";
  import { fade, scale } from "svelte/transition";
  import { quintOut } from "svelte/easing";

  let {
    open = false,
    title = "",
    size = "md",
    closeOnEscape = true,
    closeOnClickOutside = true,
    showCloseButton = true,
    preventScroll = true,
    zIndex = 99,
    content,
    footer,
    closed = () => {},
    opened = () => {},
  } = $props();

  // State
  let dialog = $state(null);
  let previouslyFocused = $state(null);

  // Size classes mapping
  const sizeClasses = {
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg",
    xl: "max-w-xl",
    "2xl": "max-w-2xl",
    "3xl": "max-w-3xl",
    "4xl": "max-w-4xl",
    "5xl": "max-w-5xl",
    "6xl": "max-w-6xl",
    "7xl": "max-w-7xl",
    full: "max-w-full",
  };

  // Handle escape key
  function handleKeydown(e) {
    if (e.key === "Escape" && closeOnEscape && open) {
      e.preventDefault();
      close();
    }
  }

  // Handle click outside
  function handleOutsideClick(e) {
    if (closeOnClickOutside && dialog && !dialog.contains(e.target) && open) {
      close();
    }
  }

  // Close modal
  function close() {
    open = false;
    closed();
  }

  // Focus trap
  function trapFocus(element) {
    const focusableElements = element.querySelectorAll(
      'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select'
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    function handleTabKey(e) {
      if (e.key === "Tab") {
        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    }

    element.addEventListener("keydown", handleTabKey);
    firstElement.focus();

    return {
      destroy() {
        element.removeEventListener("keydown", handleTabKey);
      },
    };
  }

  // Lifecycle
  $effect(() => {
    if (open) {
      previouslyFocused = document.activeElement;

      if (preventScroll) {
        document.body.classList.add("overflow-hidden");
      }

      window.addEventListener("keydown", handleKeydown);
      window.addEventListener("mousedown", handleOutsideClick);

      opened();

      // Focus trap
      if (dialog) {
        const cleanup = trapFocus(dialog);
        return () => cleanup.destroy();
      }
    } else {
      if (preventScroll) {
        document.body.classList.remove("overflow-hidden");
      }

      window.removeEventListener("keydown", handleKeydown);
      window.removeEventListener("mousedown", handleOutsideClick);

      if (previouslyFocused) {
        previouslyFocused.focus();
      }
    }
  });

  onDestroy(() => {
    if (preventScroll) {
      document.body.classList.remove("overflow-hidden");
    }

    window.removeEventListener("keydown", handleKeydown);
    window.removeEventListener("mousedown", handleOutsideClick);
  });
</script>

{#if open}
  <div
    class="fixed inset-0 flex items-center justify-center p-4"
    style="z-index: {zIndex};"
    transition:fade={{ duration: 200 }}
  >
    <!-- Backdrop -->
    <div
      class="fixed inset-0 bg-black backdrop-blur-sm"
      style="opacity: 0.75;"
      transition:fade={{ duration: 150 }}
    ></div>

    <!-- Modal -->
    <div
      bind:this={dialog}
      class="relative {sizeClasses[
        size
      ]} w-full bg-white dark:bg-gray-800 rounded-lg shadow-xl overflow-hidden flex flex-col max-h-[90vh]"
      transition:scale={{ duration: 200, easing: quintOut, start: 0.95 }}
    >
      {#if title || showCloseButton}
        <!-- Header -->
        <div
          class="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700"
        >
          {#if title}
            <h3 class="text-lg font-medium text-gray-900 dark:text-white">
              {title}
            </h3>
          {:else}
            <div></div>
          {/if}

          {#if showCloseButton}
            <button
              type="button"
              class="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg p-1.5 inline-flex items-center justify-center dark:hover:bg-gray-600 dark:hover:text-white"
              onclick={close}
              aria-label="Close"
            >
              <svg
                class="w-5 h-5"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clip-rule="evenodd"
                ></path>
              </svg>
            </button>
          {/if}
        </div>
      {/if}

      <!-- Content -->
      <div class="p-6 overflow-y-auto">
        {@render content()}
      </div>

      <!-- Footer -->
      {#if footer}
        <div
          class="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 mt-auto"
        >
          {@render footer()}
        </div>
      {/if}
    </div>
  </div>
{/if}
